import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, EmptyContent, Flex, Spinner, Table } from '@/components/ui';
import clsx from 'clsx';

import { IconFileSearch } from '@tabler/icons-react';
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { DataTablePagination } from './DataTablePagination';
import { DataTableToolbar } from './DataTableToolbar';
import { DataTableColumnMeta, DataTableProps } from './types';
import { useTablePageSize } from '@/hooks/useTablePageSize';
import { DataTableHeaderRenderer } from './DataTableHeaderRenderer';
import { DataTableCellRenderer } from './DataTableCellRenderer';
import { DataTableSkeletonCell } from './DataTableSkeletonCell';

export function DataTable<TData, TValue>({
  data,
  columns,
  pagination,
  loading = false,
  emptyMessage = 'No data available',
  emptyIcon = <IconFileSearch />,
  customEmptyContent,
  className,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  enableColumnVisibility = true,
  enableRowSelection = false,
  manualPagination = false,
  searchPlaceholder = 'Search...',
  pageSizeOptions = [10, 25, 50, 100],
  getRowId,
  onRowClick,
  showSkeletonOnLoading = true,
  skeletonRows,
  customSkeletonCell,
  toolbarLeftSlot,
  toolbarCenterSlot,
  toolbarRightSlot,
  toolbarBottomSlot,
  withDividers,
  beforeSlot,
  afterSlot,
  enableVirtualization,
  virtualizationThreshold = 100,
  noScrollArea,
  stickyHeader = true,
  scrollMode = 'container', // 'container' | 'table'
  sorting: externalSorting,
  ...props
}: DataTableProps<TData, TValue>) {
  const resolvePinnedSizePx = (
    size: unknown,
    fallback: number = 150
  ): number => {
    if (typeof size === 'number' && Number.isFinite(size)) return size;
    if (typeof size === 'string') {
      // Prefer px values, but also allow numeric strings
      const parsed = parseFloat(size);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  };
  // Global page size management with localStorage
  const { pageSize: globalPageSize, setPageSize: setGlobalPageSize } =
    useTablePageSize(pagination?.pageSize || 10);
  const prevGlobalPageSizeRef = useRef<number>(globalPageSize);

  // Internal state management
  // Use initial sorting from props if provided, otherwise empty array
  const [sortingState, setSortingState] = useState<SortingState>(
    externalSorting?.state || []
  );
  const [columnFiltersState, setColumnFiltersState] =
    useState<ColumnFiltersState>([]);
  const [globalFilterState, setGlobalFilterState] = useState<string>('');
  const [columnVisibilityState, setColumnVisibilityState] =
    useState<VisibilityState>({});
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>(
    {}
  );
  const [paginationState, setPaginationState] = useState({
    pageIndex: pagination?.pageIndex || 0,
    pageSize: pagination?.pageSize || globalPageSize,
  });

  // Track last pagination values reported to the parent to avoid infinite update loops
  // when the `pagination` prop object/handler changes identity between renders.
  const lastReportedPaginationRef = useRef<{
    pageIndex: number;
    pageSize: number;
  } | null>(null);

  // Column sizing state for resizable columns
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

  // Virtualization ref (will be initialized after table creation)
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Generate skeleton data and columns when loading
  const { skeletonData, skeletonColumns } = useMemo(() => {
    if (!loading || !showSkeletonOnLoading) {
      return { skeletonData: null, skeletonColumns: null };
    }

    // Determine number of skeleton rows
    const rows = skeletonRows || pagination?.pageSize || 10;

    // Generate skeleton data
    const skelData = Array.from({ length: rows }, (_, index) => {
      const skelRow: Record<string, unknown> = {
        __skeleton_id: index,
        id: index, // Add standard id field for getRowId
      };
      columns.forEach(col => {
        const key =
          'accessorKey' in col && typeof col.accessorKey === 'string'
            ? col.accessorKey
            : col.id || `col_${index}`;
        // Don't override id if it's already set
        if (key !== 'id') {
          skelRow[key] = null;
        }
      });
      return skelRow;
    });

    // Generate skeleton columns
    const skelColumns = columns.map(column => {
      const columnId =
        column.id ||
        ('accessorKey' in column && typeof column.accessorKey === 'string'
          ? column.accessorKey
          : 'unknown');
      const meta = column.meta as DataTableColumnMeta | undefined;

      return {
        ...column,
        cell: customSkeletonCell
          ? () => customSkeletonCell(columnId, meta)
          : () => <DataTableSkeletonCell columnId={columnId} meta={meta} />,
      };
    });

    return { skeletonData: skelData, skeletonColumns: skelColumns };
  }, [
    loading,
    showSkeletonOnLoading,
    columns,
    pagination?.pageSize,
    skeletonRows,
    customSkeletonCell,
  ]);

  // Use skeleton data/columns when loading, otherwise use real data/columns
  const finalData =
    loading && showSkeletonOnLoading && skeletonData
      ? (skeletonData as TData[])
      : data;
  const finalColumns =
    loading && showSkeletonOnLoading && skeletonColumns
      ? skeletonColumns
      : columns;

  // Create safe getRowId function for skeleton data
  const safeGetRowId = useMemo(() => {
    if (loading && showSkeletonOnLoading && skeletonData) {
      // For skeleton data, use the __skeleton_id or id
      return (row: unknown) => {
        const r = row as {
          __skeleton_id?: number | string;
          id?: number | string;
        };
        if (r.__skeleton_id !== undefined) return `skeleton_${r.__skeleton_id}`;
        if (r.id !== undefined) return `skeleton_${r.id}`;
        return `skeleton_${Math.random()}`;
      };
    }
    return getRowId; // Use original getRowId for real data
  }, [loading, showSkeletonOnLoading, skeletonData, getRowId]);

  // Table instance
  const table = useReactTable({
    data: finalData,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onSortingChange: updater => {
      const newSorting =
        typeof updater === 'function' ? updater(sortingState) : updater;
      setSortingState(newSorting);
      // Call external handler if provided
      if (externalSorting?.onSortingChange) {
        externalSorting.onSortingChange(newSorting);
      }
    },
    onColumnFiltersChange: setColumnFiltersState,
    onGlobalFilterChange: setGlobalFilterState,
    onColumnVisibilityChange: setColumnVisibilityState,
    onRowSelectionChange: setRowSelectionState,
    onPaginationChange: setPaginationState,
    onColumnSizingChange: setColumnSizing,
    state: {
      sorting: sortingState,
      columnFilters: columnFiltersState,
      globalFilter: globalFilterState,
      columnVisibility: columnVisibilityState,
      rowSelection: rowSelectionState,
      pagination: paginationState,
      columnSizing: columnSizing,
    },
    enableSorting,
    enableRowSelection,
    manualPagination,
    // For manual pagination, provide page count from server
    pageCount:
      manualPagination && pagination?.pageCount
        ? pagination.pageCount
        : undefined,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: safeGetRowId,
    ...props,
  });

  // Virtualization (after table is created)
  const rows = table.getRowModel().rows;
  const shouldVirtualize =
    enableVirtualization ?? rows.length > virtualizationThreshold;

  // Determine final noScrollArea value: use explicit prop if provided
  // For virtualization, we keep ScrollArea enabled for proper scrolling
  const finalNoScrollArea = noScrollArea ?? false;

  // Determine scroll mode: container (old behavior) or table (new with sticky header)
  const isContainerScroll = scrollMode === 'container';

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => {
      // For virtualization with ScrollArea, we need to find the viewport element
      // ScrollArea from Radix UI creates a .rt-ScrollAreaViewport element
      if (shouldVirtualize && tableContainerRef.current) {
        const viewport = tableContainerRef.current.querySelector(
          '.rt-ScrollAreaViewport'
        ) as HTMLElement | null;
        return viewport || tableContainerRef.current;
      }
      return tableContainerRef.current;
    },
    estimateSize: () => 48, // Estimated row height in pixels
    overscan: 10, // Number of rows to render outside visible area
    enabled: shouldVirtualize,
  });

  // Get virtual items (cached to avoid multiple calls in render)
  // Note: getVirtualItems() should be called on each render to get current visible items
  // but we cache it to avoid calling multiple times in the same render
  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : [];

  // Stable ref for measuring virtualized row elements
  // Using useRef to store the measurement function ensures a stable reference
  // that doesn't cause infinite re-render loops
  const measureElementRef = useRef<(node: HTMLDivElement | null) => void>();
  measureElementRef.current = (node: HTMLDivElement | null) => {
    if (node) {
      rowVirtualizer.measureElement(node);
    }
  };

  // Stable wrapper function that uses the ref to avoid dependency on rowVirtualizer
  const measureElementCallback = useCallback((node: HTMLDivElement | null) => {
    measureElementRef.current?.(node);
  }, []);

  // Helper function to normalize size values (number -> px, string -> as-is)
  // Memoized to ensure stable reference for useMemo dependencies
  const normalizeSize = React.useCallback(
    (size: number | string | undefined): string => {
      if (size === undefined) return '';
      if (typeof size === 'number') {
        return `${size}px`;
      }
      // String values like '1fr', 'auto', 'min-content' are used as-is
      return size;
    },
    []
  );

  // Helper function to normalize minSize values
  // Uses 'auto' as-is for CSS Grid compatibility (CSS Grid supports minmax(auto, ...))
  const normalizeMinSize = React.useCallback(
    (size: number | string | undefined): string => {
      if (size === undefined) return '';
      if (size === 'auto') {
        return 'auto';
      }
      return normalizeSize(size);
    },
    [normalizeSize]
  );

  // Helper function to normalize maxSize values
  // Uses 'auto' as-is for CSS Grid compatibility (CSS Grid supports minmax(..., auto))
  const normalizeMaxSize = React.useCallback(
    (size: number | string | undefined): string => {
      if (size === undefined) return '';
      if (size === 'auto') {
        return 'auto';
      }
      return normalizeSize(size);
    },
    [normalizeSize]
  );

  // Create a map of original columns by ID for fast lookup
  // This ensures we always use original meta (not skeleton) for stable grid
  const originalColumnsMap = useMemo(() => {
    const map = new Map<string, (typeof columns)[0]>();
    columns.forEach(col => {
      const id =
        col.id ||
        ('accessorKey' in col && typeof col.accessorKey === 'string'
          ? col.accessorKey
          : undefined);
      if (id) {
        map.set(id, col);
      }
    });
    return map;
  }, [columns]);

  // Generate CSS grid columns for VISIBLE columns only
  // Use original columns (not finalColumns) to ensure stable grid during loading
  // noinspection com.intellij.reactbuddy.ExhaustiveDepsInspection
  const gridColumns = useMemo(() => {
    // Use original columns to get stable meta during loading
    // This prevents grid jumping when switching from skeleton to real data
    const visibleColumns = table.getVisibleFlatColumns();
    const sizingState = table.getState().columnSizing;

    return visibleColumns
      .map(column => {
        // Get meta from original column definition (not skeleton)
        // This ensures grid stays stable during loading
        const originalColumn = originalColumnsMap.get(column.id);
        const meta =
          (originalColumn?.meta as DataTableColumnMeta | undefined) ||
          (column.columnDef.meta as DataTableColumnMeta | undefined);

        // Check if column has been resized
        const currentSize = sizingState[column.id];

        // Use current size if available, otherwise use meta information
        if (currentSize) {
          return `${currentSize}px`;
        }

        // Priority 1: New size system (minSize/maxSize/size)
        if (meta?.size === 'auto') {
          // If no minSize/maxSize constraints, use native CSS 'auto'
          if (!meta.minSize && !meta.maxSize) {
            return 'auto';
          }
          // If minSize or maxSize are specified, use minmax() to preserve resizing constraints
          const minSize = meta.minSize
            ? normalizeMinSize(meta.minSize)
            : 'min-content';
          const maxSize = meta.maxSize ? normalizeMaxSize(meta.maxSize) : '1fr';
          return `minmax(${minSize}, ${maxSize})`;
        }

        // Handle explicit size (number or string like '1fr')
        if (meta?.size !== undefined && meta.size !== 'auto') {
          const sizeValue = normalizeSize(meta.size);
          const minSize = meta.minSize
            ? normalizeMinSize(meta.minSize)
            : sizeValue;
          const maxSize = meta.maxSize
            ? normalizeMaxSize(meta.maxSize)
            : sizeValue;
          if (minSize === maxSize) {
            return minSize;
          }
          return `minmax(${minSize}, ${maxSize})`;
        }

        // Handle minSize/maxSize without explicit size
        if (meta?.minSize !== undefined || meta?.maxSize !== undefined) {
          const minSize = meta.minSize
            ? normalizeMinSize(meta.minSize)
            : 'min-content';
          const maxSize = meta.maxSize ? normalizeMaxSize(meta.maxSize) : '1fr';
          return `minmax(${minSize}, ${maxSize})`;
        }

        // Priority 2: Legacy width system (for backward compatibility)
        if (meta?.width) {
          return meta.width;
        }
        if (meta?.minWidth && meta?.maxWidth) {
          return `minmax(${meta.minWidth}, ${meta.maxWidth})`;
        }
        if (meta?.minWidth) {
          return `minmax(${meta.minWidth}, 1fr)`;
        }

        // Default: flexible column
        return '1fr';
      })
      .join(' ');
  }, [
    table,
    columnSizing,
    columnVisibilityState,
    originalColumnsMap,
    normalizeSize,
    normalizeMinSize,
    normalizeMaxSize,
  ]);

  // Generate CSS classes for table state
  const isResizing = table.getState().columnSizingInfo.isResizingColumn;
  const isEmpty =
    !loading || !showSkeletonOnLoading
      ? table.getRowModel().rows?.length === 0
      : false;
  const tableClasses = useMemo(() => {
    const classes = ['data-table'];

    // Add resizing class if any column is being resized
    if (isResizing) {
      classes.push('data-table-resizing');
    }

    // Add skeleton class when loading
    if (loading && showSkeletonOnLoading) {
      classes.push('data-table-skeleton');
    }

    // Add empty class when table has no data
    if (isEmpty) {
      classes.push('data-table-empty');
    }

    // Add custom className if provided
    if (className) {
      classes.push(className);
    }

    return classes.join(' ');
  }, [isResizing, loading, showSkeletonOnLoading, isEmpty, className]);

  // Generate CSS custom properties for advanced column control
  const tableCustomProperties = useMemo(() => {
    const visibleColumns = table.getVisibleFlatColumns();
    const sizingState = table.getState().columnSizing;
    const properties: Record<string, string> = {
      '--table-columns': gridColumns,
    };

    // Add individual column properties for advanced styling (visible columns only)
    visibleColumns.forEach((column, index) => {
      // Get meta from original column definition (not skeleton) for stability
      const originalColumn = originalColumnsMap.get(column.id);
      const meta =
        (originalColumn?.meta as DataTableColumnMeta | undefined) ||
        (column.columnDef.meta as DataTableColumnMeta | undefined);

      const currentSize = sizingState[column.id];

      // Use current size if available, otherwise use meta information
      if (currentSize) {
        properties[`--table-column-${index}-width`] = `${currentSize}px`;
      } else if (meta?.size !== undefined && meta.size !== 'auto') {
        properties[`--table-column-${index}-width`] = normalizeSize(meta.size);
      } else if (meta?.width) {
        properties[`--table-column-${index}-width`] = meta.width;
      }

      // Min width
      if (meta?.minSize !== undefined) {
        properties[`--table-column-${index}-min-width`] = normalizeMinSize(
          meta.minSize
        );
      } else if (meta?.minWidth) {
        properties[`--table-column-${index}-min-width`] = meta.minWidth;
      }

      // Max width
      if (meta?.maxSize !== undefined) {
        properties[`--table-column-${index}-max-width`] = normalizeMaxSize(
          meta.maxSize
        );
      } else if (meta?.maxWidth) {
        properties[`--table-column-${index}-max-width`] = meta.maxWidth;
      }
    });

    return properties;
  }, [
    table,
    gridColumns,
    columnSizing,
    columnVisibilityState,
    originalColumnsMap,
    normalizeSize,
    normalizeMinSize,
    normalizeMaxSize,
  ]);

  // Handle pagination changes
  React.useEffect(() => {
    const onPaginationChange = pagination?.onPaginationChange;
    if (!onPaginationChange) return;

    const next = {
      pageIndex: paginationState.pageIndex,
      pageSize: paginationState.pageSize,
    };

    const last = lastReportedPaginationRef.current;
    if (
      last &&
      last.pageIndex === next.pageIndex &&
      last.pageSize === next.pageSize
    ) {
      return;
    }

    lastReportedPaginationRef.current = next;
    onPaginationChange(next);
  }, [
    paginationState.pageIndex,
    paginationState.pageSize,
    pagination?.onPaginationChange,
  ]);

  // Note: we intentionally do NOT clamp pageIndex here.
  // For server-side pagination, clamping based on a stale pageCount can prevent navigation
  // and break loading skeletons during page transitions. Page correction should happen
  // after receiving the server response (e.g., using response.current_page).

  // Save page size to localStorage when it changes (only if not controlled externally)
  React.useEffect(() => {
    if (!pagination?.pageSize && paginationState.pageSize !== globalPageSize) {
      setGlobalPageSize(paginationState.pageSize);
    }
  }, [
    paginationState.pageSize,
    globalPageSize,
    setGlobalPageSize,
    pagination?.pageSize,
  ]);

  // Update pagination state when global page size changes (from other tables)
  React.useEffect(() => {
    // Prevent swap-loop:
    // When the user changes local pageSize, globalPageSize still has the old value
    // during this commit. We only want to sync global -> local when globalPageSize
    // actually changed (e.g., from another table/tab), not just when it differs.
    if (prevGlobalPageSizeRef.current === globalPageSize) {
      return;
    }
    prevGlobalPageSizeRef.current = globalPageSize;

    if (!pagination?.pageSize && globalPageSize !== paginationState.pageSize) {
      setPaginationState(prev => ({
        ...prev,
        pageSize: globalPageSize,
        pageIndex: 0, // Reset to first page when page size changes
      }));
    }
  }, [globalPageSize, pagination?.pageSize, paginationState.pageSize]);

  // Only show loading fallback if skeleton is disabled
  if (loading && !showSkeletonOnLoading) {
    return (
      <EmptyContent
        variant="outlined"
        withIconBox
        size="small"
        title="Loading table data..."
        description="Please wait while the table data is loaded."
        icon={<Spinner />}
      />
    );
  }

  return (
    <Flex
      gap="4"
      direction="column"
      className={clsx('data-table', {
        'data-table--container-scroll': isContainerScroll,
        'data-table--table-scroll': !isContainerScroll,
      })}
      style={{
        height: isContainerScroll ? 'auto' : '100%',
        minHeight: isContainerScroll ? 'auto' : 0,
      }}
    >
      {(beforeSlot ||
        enableFiltering ||
        enableColumnVisibility ||
        toolbarLeftSlot ||
        toolbarCenterSlot ||
        toolbarRightSlot ||
        toolbarBottomSlot) && (
        <Flex
          gap="2"
          direction="column"
          className="data-table__sticked-header"
          style={{ flexShrink: 0 }}
        >
          {beforeSlot}
          {/* Toolbar */}
          {(enableFiltering ||
            enableColumnVisibility ||
            toolbarLeftSlot ||
            toolbarCenterSlot ||
            toolbarRightSlot ||
            toolbarBottomSlot) && (
            <DataTableToolbar
              table={table}
              globalFilter={globalFilterState}
              onGlobalFilterChange={setGlobalFilterState}
              searchPlaceholder={searchPlaceholder}
              leftSlot={toolbarLeftSlot}
              centerSlot={toolbarCenterSlot}
              rightSlot={toolbarRightSlot}
              bottomSlot={toolbarBottomSlot}
              enableColumnVisibility={enableColumnVisibility}
            />
          )}
        </Flex>
      )}

      {/* Table */}
      <Flex
        direction="column"
        flexGrow="1"
        ref={tableContainerRef}
        style={{
          height: isContainerScroll
            ? 'auto'
            : shouldVirtualize
              ? '600px'
              : '100%',
          maxHeight: isContainerScroll ? 'none' : '100%', // Ensure container has height constraint for sticky to work
          minHeight: isContainerScroll ? 'auto' : 0, // Allow flex shrinking - critical for flex children
          // flex: isContainerScroll ? '1' : '1 1 0', // Ensure flex takes available space
          // When ScrollArea is enabled, don't set overflow here to avoid double scrolling
          overflow: shouldVirtualize && finalNoScrollArea ? 'auto' : 'visible',
        }}
      >
        <Table.Root
          withDividers={Boolean(withDividers) || Boolean(isResizing)}
          columns={gridColumns}
          className={tableClasses}
          style={tableCustomProperties as React.CSSProperties}
          noScrollArea={finalNoScrollArea}
        >
          <Table.Header sticky={stickyHeader}>
            {table.getHeaderGroups().map(headerGroup => {
              // Calculate pinned column positions
              const visibleHeaders = headerGroup.headers.filter(
                h => !h.isPlaceholder
              );
              const pinnedLeftColumns: string[] = [];
              const pinnedRightColumns: string[] = [];

              // First pass: identify pinned columns and calculate left offsets
              visibleHeaders.forEach(header => {
                const meta = header.column.columnDef.meta as
                  | DataTableColumnMeta
                  | undefined;
                if (meta?.pinned === 'left') {
                  pinnedLeftColumns.push(header.id);
                }
              });

              // Second pass: calculate right offsets (reverse order)
              for (let i = visibleHeaders.length - 1; i >= 0; i--) {
                const header = visibleHeaders[i];
                const meta = header.column.columnDef.meta as
                  | DataTableColumnMeta
                  | undefined;
                if (meta?.pinned === 'right') {
                  pinnedRightColumns.push(header.id);
                }
              }

              return (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const canResize = header.column.getCanResize();
                    const meta = header.column.columnDef.meta as
                      | DataTableColumnMeta
                      | undefined;
                    const isPinnedLeft = meta?.pinned === 'left';
                    const isPinnedRight = meta?.pinned === 'right';

                    // Calculate left offset for pinned left columns
                    let leftPosition = 0;
                    if (isPinnedLeft) {
                      const pinnedIndex = pinnedLeftColumns.indexOf(header.id);
                      for (let i = 0; i < pinnedIndex; i++) {
                        const prevHeader = visibleHeaders.find(
                          h => h.id === pinnedLeftColumns[i]
                        );
                        if (prevHeader) {
                          const prevMeta = prevHeader.column.columnDef.meta as
                            | DataTableColumnMeta
                            | undefined;
                          const prevSize =
                            table.getState().columnSizing[prevHeader.id] ||
                            resolvePinnedSizePx(
                              prevMeta?.size ?? prevMeta?.minSize,
                              150
                            );
                          leftPosition += prevSize;
                        }
                      }
                    }

                    // Calculate right offset for pinned right columns
                    let rightPosition = 0;
                    if (isPinnedRight) {
                      const pinnedIndex = pinnedRightColumns.indexOf(header.id);
                      for (
                        let i = pinnedIndex + 1;
                        i < pinnedRightColumns.length;
                        i++
                      ) {
                        const nextHeader = visibleHeaders.find(
                          h => h.id === pinnedRightColumns[i]
                        );
                        if (nextHeader) {
                          const nextMeta = nextHeader.column.columnDef.meta as
                            | DataTableColumnMeta
                            | undefined;
                          const nextSize =
                            table.getState().columnSizing[nextHeader.id] ||
                            resolvePinnedSizePx(
                              nextMeta?.size ?? nextMeta?.minSize,
                              150
                            );
                          rightPosition += nextSize;
                        }
                      }
                    }

                    const pinnedClasses = [
                      isPinnedLeft ? 'data-table-pinned-left' : '',
                      isPinnedRight ? 'data-table-pinned-right' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <Table.HeaderCell
                        key={header.id}
                        className={`
                          data-table-header-cell
                          data-table-column-${index}
                          ${canResize ? 'data-table-resizable' : ''}
                          ${pinnedClasses}
                        `.trim()}
                        style={{
                          ...(isPinnedLeft && {
                            left: `${leftPosition}px`,
                            zIndex: 2 + pinnedLeftColumns.indexOf(header.id),
                          }),
                          ...(isPinnedRight && {
                            right: `${rightPosition}px`,
                            zIndex:
                              2 +
                              (pinnedRightColumns.length -
                                pinnedRightColumns.indexOf(header.id)),
                          }),
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <DataTableHeaderRenderer header={header} />
                        )}

                        {/* Resize handle */}
                        {canResize && (
                          <div
                            className={`data-table-resize-handle ${header.column.getIsResizing() ? 'is-resizing' : ''}`}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              height: '100%',
                              width: '5px',
                              cursor: 'col-resize',
                              userSelect: 'none',
                              touchAction: 'none',
                            }}
                          />
                        )}
                      </Table.HeaderCell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Header>
          <Table.Body
            style={
              shouldVirtualize
                ? {
                    minHeight: rowVirtualizer.getTotalSize(),
                  }
                : undefined
            }
          >
            {rows?.length ? (
              <>
                {/* Spacer for rows above visible area (only when virtualizing) */}
                {shouldVirtualize &&
                  virtualItems.length > 0 &&
                  virtualItems[0].start > 0 && (
                    <div
                      className="table__virtualization-spacer"
                      style={{
                        gridColumn: '1 / -1',
                        height: virtualItems[0].start,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                {/* Visible rows in normal flow */}
                {(shouldVirtualize
                  ? virtualItems
                  : rows.map((_, i) => ({ index: i }))
                ).map(virtualRow => {
                  const row = rows[virtualRow.index];
                  if (!row) return null;
                  // Calculate pinned column positions for cells
                  const visibleCells = row.getVisibleCells();
                  const pinnedLeftColumns: string[] = [];
                  const pinnedRightColumns: string[] = [];

                  // First pass: identify pinned columns
                  visibleCells.forEach(cell => {
                    const meta = cell.column.columnDef.meta as
                      | DataTableColumnMeta
                      | undefined;
                    if (meta?.pinned === 'left') {
                      pinnedLeftColumns.push(cell.column.id);
                    }
                  });

                  // Second pass: calculate right offsets (reverse order)
                  for (let i = visibleCells.length - 1; i >= 0; i--) {
                    const cell = visibleCells[i];
                    const meta = cell.column.columnDef.meta as
                      | DataTableColumnMeta
                      | undefined;
                    if (meta?.pinned === 'right') {
                      pinnedRightColumns.push(cell.column.id);
                    }
                  }

                  return (
                    <Table.Row
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={() => onRowClick?.(row)}
                      style={{
                        ...(onRowClick ? { cursor: 'pointer' } : undefined),
                      }}
                      data-index={virtualRow.index}
                      ref={
                        shouldVirtualize ? measureElementCallback : undefined
                      }
                    >
                      {visibleCells.map(cell => {
                        const meta = cell.column.columnDef.meta as
                          | DataTableColumnMeta
                          | undefined;
                        const isPinnedLeft = meta?.pinned === 'left';
                        const isPinnedRight = meta?.pinned === 'right';

                        // Calculate left offset for pinned left columns
                        let leftPosition = 0;
                        if (isPinnedLeft) {
                          const pinnedIndex = pinnedLeftColumns.indexOf(
                            cell.column.id
                          );
                          for (let i = 0; i < pinnedIndex; i++) {
                            const prevCell = visibleCells.find(
                              c => c.column.id === pinnedLeftColumns[i]
                            );
                            if (prevCell) {
                              const prevMeta = prevCell.column.columnDef
                                .meta as DataTableColumnMeta | undefined;
                              const prevSize =
                                table.getState().columnSizing[
                                  prevCell.column.id
                                ] ||
                                resolvePinnedSizePx(
                                  prevMeta?.size ?? prevMeta?.minSize,
                                  150
                                );
                              leftPosition += prevSize;
                            }
                          }
                        }

                        // Calculate right offset for pinned right columns
                        let rightPosition = 0;
                        if (isPinnedRight) {
                          const pinnedIndex = pinnedRightColumns.indexOf(
                            cell.column.id
                          );
                          for (
                            let i = pinnedIndex + 1;
                            i < pinnedRightColumns.length;
                            i++
                          ) {
                            const nextCell = visibleCells.find(
                              c => c.column.id === pinnedRightColumns[i]
                            );
                            if (nextCell) {
                              const nextMeta = nextCell.column.columnDef
                                .meta as DataTableColumnMeta | undefined;
                              const nextSize =
                                table.getState().columnSizing[
                                  nextCell.column.id
                                ] ||
                                resolvePinnedSizePx(
                                  nextMeta?.size ?? nextMeta?.minSize,
                                  150
                                );
                              rightPosition += nextSize;
                            }
                          }
                        }

                        const pinnedClasses = [
                          isPinnedLeft ? 'data-table-pinned-left' : '',
                          isPinnedRight ? 'data-table-pinned-right' : '',
                        ]
                          .filter(Boolean)
                          .join(' ');

                        // Check if column has size: 'auto'
                        // Use original column meta for consistency (not skeleton)
                        const originalColumn = originalColumnsMap.get(
                          cell.column.id
                        );
                        const cellMeta =
                          (originalColumn?.meta as
                            | DataTableColumnMeta
                            | undefined) ||
                          (cell.column.columnDef.meta as
                            | DataTableColumnMeta
                            | undefined);
                        const isAutoSize = cellMeta?.size === 'auto';
                        const autoSizeClass = isAutoSize
                          ? 'data-table-cell-auto'
                          : '';

                        const cellClasses = [pinnedClasses, autoSizeClass]
                          .filter(Boolean)
                          .join(' ');

                        return (
                          <Table.Cell
                            key={cell.id}
                            className={cellClasses}
                            style={{
                              ...(isPinnedLeft && {
                                left: `${leftPosition}px`,
                                zIndex:
                                  1 + pinnedLeftColumns.indexOf(cell.column.id),
                              }),
                              ...(isPinnedRight && {
                                right: `${rightPosition}px`,
                                zIndex:
                                  1 +
                                  (pinnedRightColumns.length -
                                    pinnedRightColumns.indexOf(cell.column.id)),
                              }),
                            }}
                          >
                            <DataTableCellRenderer cell={cell} table={table} />
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  );
                })}

                {/* Spacer for rows below visible area (only when virtualizing) */}
                {shouldVirtualize &&
                  virtualItems.length > 0 &&
                  (() => {
                    const totalSize = rowVirtualizer.getTotalSize();
                    const lastItem = virtualItems[virtualItems.length - 1];
                    const remainingHeight = totalSize - lastItem.end;
                    return remainingHeight > 0 ? (
                      <div
                        className="table__virtualization-spacer"
                        style={{
                          gridColumn: '1 / -1',
                          height: remainingHeight,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null;
                  })()}
              </>
            ) : (
              <Table.Row className="data-table-empty-row">
                <Table.Cell className="data-table-empty-cell">
                  {globalFilterState.trim() && data.length > 0 ? (
                    // Show search-specific empty state when there's an active filter and original data exists
                    <EmptyContent
                      className="data-table-empty"
                      size="medium"
                      icon={<IconFileSearch />}
                      title="No results found"
                      description={`No items match your search "${globalFilterState}". Try adjusting your search terms.`}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        type="button"
                        className="btn btn--outlined btn--small"
                        onClick={() => setGlobalFilterState('')}
                      >
                        Clear search
                      </Button>
                    </EmptyContent>
                  ) : (
                    // Show default empty content when there's no active filter or no data at all
                    customEmptyContent || (
                      <EmptyContent
                        className="data-table-empty"
                        size="medium"
                        icon={emptyIcon}
                        description={emptyMessage}
                      />
                    )
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Flex>

      {/* Pagination */}
      {(enablePagination || afterSlot) && (
        <Flex direction="column" gap="2" className="data-table__sticked-footer">
          {enablePagination && (
            <DataTablePagination
              table={table}
              pageSizeOptions={pageSizeOptions}
              totalCount={pagination?.totalCount}
              manualPagination={manualPagination}
            />
          )}
          {afterSlot}
        </Flex>
      )}
    </Flex>
  );
}
